/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-10-06 11:39:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Modal, Icon, TextField, Spinner, TextStyle } from '@shopify/polaris';
import { SearchMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import InfiniteScroll from 'react-infinite-scroll-component';

import ListOptions from 'src/components/views/recommendation/widget/item/general/select-product-modal/list-options';
import { NUMBER_PRODUCT_PER_PAGE } from 'src/constants/recommendation';
import type { Product } from 'src/models/product';
import { prettyText } from 'src/helpers';
import * as productActions from 'src/stores/actions/product-actions';
import type { RuleGroupListType } from 'src/models/recommendation';

type Props = {
  active: boolean,
  toggleModal: () => void,
  limitSelection?: number,
  fieldName: string,
  selectedIndex: number,
  selectedProducts: Array<Product>,
  rules: RuleGroupListType,
  updateRulesField: Function
};

type ComposedProps = Props & WithI18nProps;
type State = {
  options: Array<Object>,
  selectedOptions: Array<Object>,
  inputValue: string,
  skip: number,
  limit: number,
  hasMore: boolean,
  excludedProductIds: string
};
// DONE
class SelectProductModal extends Component<ComposedProps, State> {
  timerTyping = undefined;

  state = {
    options: [],
    selectedOptions: [],
    inputValue: '',
    skip: 0,
    limit: NUMBER_PRODUCT_PER_PAGE,
    hasMore: false,
    excludedProductIds: ''
  };

  componentDidMount() {
    const { selectedProducts } = this.props;
    this.setState({
      selectedOptions: [...selectedProducts]
    });
  }

  componentWillReceiveProps(nextProps) {
    const {
      loading: nextLoading,
      list,
      total,
      active: nextActive,
      selectedProducts: nextSelectedProducts = []
    } = nextProps;
    const { loading, active, selectedProducts } = this.props;
    const { options, skip, limit, selectedOptions } = this.state;
    // Close modal
    if (active && !nextActive) {
      // Reset states
      this.setState({
        options: [],
        selectedOptions: [...nextSelectedProducts],
        inputValue: '',
        skip: 0,
        hasMore: false
      });
    }
    let state = {};
    // Open modal
    if (!active && nextActive) {
      const { getProducts } = this.props;
      const excludedProductIds = this.getListExcludedProductIds(nextProps);
      state.excludedProductIds = excludedProductIds;
      getProducts('', skip, limit, excludedProductIds);
    }
    // Update state from API
    if (loading && !nextLoading && nextActive) {
      const listProducts = _.map(list, product => ({
        ...product,
        isSelected: Boolean(_.find(selectedOptions, { id: product.id }))
      }));
      // Avoid duplicate product on modal when making call many APIs return the same product
      state.options = this.unionProducts(options, listProducts);
      state.hasMore = skip + limit < total;
    }
    // Update selected option
    if (selectedProducts !== nextSelectedProducts) {
      state.selectedOptions = [...nextSelectedProducts];
    }
    this.setState(state);
  }

  /**
   * Get list of product ids which is exist in the another rule
   * @returns Array<number>
   */
  getListExcludedProductIds = props => {
    const { fieldName, selectedIndex, rules } = props;
    if (fieldName !== 'conditions') {
      return [];
    }

    let result = [];
    _.map(rules, (rule, index) => {
      if (index !== selectedIndex) {
        const value = rule.conditions[0]?.value;
        if (!_.isEmpty(value)) {
          const ids = _.map(value, item => item.id).filter(id => id);
          result = [...result, ...ids];
        }
      }
    });
    return result.join(',');
  };
  /**
   * Union product list by id and variant id
   * @param {*} currentList
   * @param {*} newList
   * @returns
   */
  unionProducts = (currentList, newList) => {
    const unionList = currentList;
    newList.forEach(ele => {
      const { id, product_id } = ele;
      if (!_.find(unionList, { id, product_id })) {
        unionList.push(ele);
      }
    });

    return unionList;
  };

  componentWillUnmount() {
    // Reset states
    this.setState({
      options: [],
      selectedOptions: [],
      inputValue: '',
      skip: 0,
      hasMore: false
    });
  }

  render() {
    const { active, loading, i18n, limitSelection, toggleModal } = this.props;
    const { options, hasMore, selectedOptions, inputValue } = this.state;
    const selectedOptionsLength = selectedOptions.length;
    const isHaveLimitSelection = _.isNumber(limitSelection) && limitSelection > 0;

    return (
      <Modal
        title={i18n.translate('AdminHTML.Recommendation.SelectProductModal.title')}
        open={active}
        footer={
          isHaveLimitSelection
            ? i18n.translate('AdminHTML.Recommendation.SelectProductModal.footer', {
                nt1_number: selectedOptionsLength,
                nt2_number: limitSelection,
                t: i18n.translate('AdminHTML.Recommendation.SelectProductModal.footer_t', {
                  count: selectedOptionsLength < 1 ? 1 : selectedOptionsLength
                })
              })
            : null
        }
        onClose={toggleModal}
        noScroll
        primaryAction={{
          content: i18n.translate('AdminHTML.Recommendation.SelectProductModal.add'),
          onAction: this.handleAddProducts,
          disabled: _.isEmpty(selectedOptions)
        }}
        secondaryActions={[
          {
            content: i18n.translate('AdminHTML.Common.Action.cancel'),
            onAction: toggleModal
          }
        ]}>
        <div className="BC-Page__SelectProductModal">
          <Modal.Section>
            <div>
              <p style={{ marginBottom: '1rem' }}>
                {i18n.translate('AdminHTML.Recommendation.SelectProductModal.limitText', {
                  t1: (
                    <TextStyle variation="strong">
                      {i18n.translate('AdminHTML.Recommendation.SelectProductModal.limitText_t1', {
                        nt_limit: limitSelection
                      })}
                    </TextStyle>
                  )
                })}
              </p>
              <div
                className="search-product-suggestion"
                onKeyDown={this.handleKeyDown}
                onKeyUp={this.handleKeyUp}>
                <TextField
                  onChange={this.onTextChange}
                  value={inputValue}
                  prefix={<Icon source={SearchMinor} color="base" />}
                  placeholder={i18n.translate('AdminHTML.Recommendation.SelectProductModal.search')}
                />
              </div>

              <div id="infiniteScroll_Wrapper_ModalSelectProduct">
                <InfiniteScroll
                  dataLength={options.length}
                  next={this.loadMore}
                  scrollableTarget="infiniteScroll_Wrapper_ModalSelectProduct"
                  hasMore={hasMore}
                  loader={
                    <div key={'spinner'} style={{ textAlign: 'center', padding: '9px 0 5px 0' }}>
                      <Spinner size="small" />
                    </div>
                  }>
                  <ListOptions
                    loading={loading}
                    options={options}
                    onSelect={this.onSelect}
                    isFull={isHaveLimitSelection ? selectedOptionsLength === limitSelection : false}
                  />
                </InfiniteScroll>
              </div>
            </div>
          </Modal.Section>
        </div>
      </Modal>
    );
  }

  // handle add product
  handleAddProducts = () => {
    const { selectedOptions } = this.state;
    const { selectedProducts, updateRulesField, selectedIndex, fieldName, rules, toggleModal } =
      this.props;

    // Build new product list from list of selected product, keep order of old products and add new products in the bottom
    // Get general items in selectedProducts and selectedOptions
    let newSelectedProducts = _.filter(
      selectedProducts,
      item =>
        _.findIndex(selectedOptions, product => {
          return product.id === item.id;
        }) >= 0
    );
    // Add new items from product list
    newSelectedProducts = _.unionBy(newSelectedProducts, selectedOptions, 'id');

    let clonedRules = _.cloneDeep(rules);
    clonedRules[selectedIndex][fieldName][0].value = newSelectedProducts;

    updateRulesField(clonedRules);

    toggleModal();
  };

  // Key press event on search field
  handleKeyDown = event => {
    clearTimeout(this.timerTyping);
  };

  // Key up event, start searching after 500ms
  handleKeyUp = event => {
    const { getProducts } = this.props;
    const { limit, inputValue, excludedProductIds } = this.state;
    let value = inputValue.toLowerCase().trim();
    value = prettyText(value);

    // Invoke search request after 500ms
    this.timerTyping = setTimeout(() => {
      this.setState(
        {
          skip: 0,
          options: [],
          hasMore: false
        },
        () => {
          getProducts(value, 0, limit, excludedProductIds);
        }
      );
    }, 500);
  };

  onTextChange = value => {
    this.setState({
      inputValue: value
    });
  };

  onSelect = index => {
    let { options, selectedOptions } = this.state;
    const isSelected = options[index].isSelected;

    if (isSelected) {
      const { id } = options[index];
      _.remove(selectedOptions, item => {
        return item.id === id;
      });
    } else {
      selectedOptions.push(options[index]);
    }
    options[index].isSelected = !options[index].isSelected;

    this.setState({ options, selectedOptions });
  };

  // Read more items
  loadMore = () => {
    const { skip, limit, inputValue, excludedProductIds } = this.state;
    const { getProducts } = this.props;
    const nextSkip = skip + limit;

    // Set skip, limit for next page
    this.setState({ skip: nextSkip, limit });
    getProducts(inputValue || '', nextSkip, limit, excludedProductIds);
  };
}

const mapStateToProps = (state, props) => {
  return {
    list: _.get(state.product, 'products', []),
    total: _.get(state.product, 'total', 0),
    loading: _.get(state.product, 'loading', false)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getProducts: (q, skip, limit, excludedProductIds) =>
    dispatch(
      productActions.getProducts(
        {
          q: encodeURIComponent(q),
          skip,
          limit,
          feature: 'recommendation',
          excludedProductIds
        },
        ownProps.i18n
      )
    )
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SelectProductModal));
